import React, { useEffect, useState } from 'react';
import api from '../api';

const STATUS_COLOURS = {
  active: '#22c55e',
  completed: '#94a3b8'
};

export default function Timesheets() {
  const [tab, setTab] = useState('clockin');
  const [projects, setProjects] = useState([]);
  const [operations, setOperations] = useState([]);
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ project_id: '', operation_id: '', worker_name: '', notes: '' });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.value || []));
    api.get('/operations').then(r => setOperations(r.data.value || []));
    loadActive();
    loadHistory();
  }, []);

  const loadActive = () => {
    api.get('/timesheets/active').then(r => setActive(r.data.value || []));
  };

  const loadHistory = () => {
    api.get('/timesheets').then(r => setHistory((r.data.value || []).filter(e => e.ended_at)));
  };

  const handleClockIn = async () => {
    if (!form.project_id || !form.operation_id || !form.worker_name) {
      setMessage('Please fill in Project, Operation and Name.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/timesheets/clock-in', {
        project_id: parseInt(form.project_id),
        operation_id: parseInt(form.operation_id),
        worker_name: form.worker_name,
        notes: form.notes
      });
      setMessage('Clocked in successfully.');
      setForm({ project_id: '', operation_id: '', worker_name: '', notes: '' });
      loadActive();
    } catch (e) {
      setMessage('Error: ' + (e.response?.data?.error || e.message));
    }
    setLoading(false);
  };

  const handleClockOut = async (id) => {
    try {
      await api.patch(`/timesheets/${id}/clock-out`);
      loadActive();
      loadHistory();
      setMessage('Clocked out successfully.');
    } catch (e) {
      setMessage('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const formatDuration = (mins) => {
    if (!mins) return '-';
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const formatTime = (ts) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ padding: '2rem', color: '#f1f5f9' }}>
      <h1 style={{ color: '#f59e0b', marginBottom: '0.25rem' }}>Timesheets</h1>
      <p style={{ color: '#94a3b8', marginBottom: '1.5rem' }}>Shop floor time tracking</p>

      {message && (
        <div style={{ background: '#1e293b', border: '1px solid #f59e0b', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', color: '#f59e0b' }}>
          {message}
          <button onClick={() => setMessage('')} style={{ float: 'right', background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {[['clockin', 'Clock In / Out'], ['history', 'History']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '0.5rem 1.25rem', borderRadius: 6, border: 'none', cursor: 'pointer',
            background: tab === key ? '#f59e0b' : '#1e293b',
            color: tab === key ? '#0f172a' : '#94a3b8', fontWeight: 600
          }}>{label}</button>
        ))}
      </div>

      {/* Clock In Tab */}
      {tab === 'clockin' && (
        <div>
          {/* Clock In Form */}
          <div style={{ background: '#1e293b', borderRadius: 10, padding: '1.5rem', marginBottom: '2rem', maxWidth: 500 }}>
            <h3 style={{ margin: '0 0 1rem', color: '#f59e0b' }}>New Entry</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <select value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })}
                style={{ padding: '0.6rem', borderRadius: 6, background: '#0f172a', color: '#f1f5f9', border: '1px solid #334155' }}>
                <option value=''>Select Project...</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.job_number} — {p.title}</option>)}
              </select>
              <select value={form.operation_id} onChange={e => setForm({ ...form, operation_id: e.target.value })}
                style={{ padding: '0.6rem', borderRadius: 6, background: '#0f172a', color: '#f1f5f9', border: '1px solid #334155' }}>
                <option value=''>Select Operation...</option>
                {operations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              <input placeholder='Your name' value={form.worker_name} onChange={e => setForm({ ...form, worker_name: e.target.value })}
                style={{ padding: '0.6rem', borderRadius: 6, background: '#0f172a', color: '#f1f5f9', border: '1px solid #334155' }} />
              <input placeholder='Notes (optional)' value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                style={{ padding: '0.6rem', borderRadius: 6, background: '#0f172a', color: '#f1f5f9', border: '1px solid #334155' }} />
              <button onClick={handleClockIn} disabled={loading}
                style={{ padding: '0.75rem', borderRadius: 6, background: '#22c55e', color: '#fff', border: 'none', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' }}>
                ⏱ Clock In
              </button>
            </div>
          </div>

          {/* Active Entries */}
          <h3 style={{ color: '#f59e0b', marginBottom: '1rem' }}>Currently Clocked In ({active.length})</h3>
          {active.length === 0 ? (
            <p style={{ color: '#94a3b8' }}>No active entries.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {active.map(e => (
                <div key={e.id} style={{ background: '#1e293b', borderRadius: 8, padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>{e.worker_name}</div>
                    <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>{e.job_number} — {e.project_title}</div>
                    <div style={{ color: '#f59e0b', fontSize: '0.85rem' }}>{e.operation}</div>
                    <div style={{ color: '#64748b', fontSize: '0.8rem' }}>Started: {formatTime(e.started_at)}</div>
                  </div>
                  <button onClick={() => handleClockOut(e.id)}
                    style={{ padding: '0.5rem 1rem', borderRadius: 6, background: '#ef4444', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
                    Clock Out
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {tab === 'history' && (
        <div>
          <h3 style={{ color: '#f59e0b', marginBottom: '1rem' }}>Completed Entries ({history.length})</h3>
          {history.length === 0 ? (
            <p style={{ color: '#94a3b8' }}>No completed entries yet.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #334155', color: '#94a3b8', textAlign: 'left' }}>
                    <th style={{ padding: '0.6rem' }}>Worker</th>
                    <th style={{ padding: '0.6rem' }}>Project</th>
                    <th style={{ padding: '0.6rem' }}>Operation</th>
                    <th style={{ padding: '0.6rem' }}>Started</th>
                    <th style={{ padding: '0.6rem' }}>Duration</th>
                    <th style={{ padding: '0.6rem' }}>Labour Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(e => (
                    <tr key={e.id} style={{ borderBottom: '1px solid #1e293b' }}>
                      <td style={{ padding: '0.6rem' }}>{e.worker_name}</td>
                      <td style={{ padding: '0.6rem', color: '#94a3b8' }}>{e.job_number} — {e.project_title}</td>
                      <td style={{ padding: '0.6rem', color: '#f59e0b' }}>{e.operation}</td>
                      <td style={{ padding: '0.6rem', color: '#64748b' }}>{formatTime(e.started_at)}</td>
                      <td style={{ padding: '0.6rem' }}>{formatDuration(e.duration_minutes)}</td>
                      <td style={{ padding: '0.6rem', color: '#22c55e' }}>${parseFloat(e.labour_cost || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
